import React, { useEffect, useState } from "react";

import axios from "axios";
import { isEmpty } from "lodash-es";
import { Controller, FormProvider, useForm } from "react-hook-form";
import slugify from "slugify";

import { Flow } from "src/api/flowTypes";
import { Textarea } from "src/base-components/Textarea";
import { FlowReviewConfiguration } from "src/clients/flow-api";
import { WorkspaceSimpleRoleType } from "src/clients/taktile-api";
import { Button } from "src/design-system/Button";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Modal } from "src/design-system/Modal";
import { NameInput } from "src/flowsOverview/FlowNameInput";
import { FlowReviewConfigEditor } from "src/flowsOverview/FlowReviewConfigEditor";
import { FlowReviewConfigPreview } from "src/flowsOverview/FlowReviewConfigPreview";
import { SlugInput } from "src/flowsOverview/FlowSlugInput";
import {
  DecisionFlowInputs,
  TestRunConfiguration,
} from "src/flowsOverview/types";
import { FOLDER_NAME_NOT_AVAILABLE_MESSAGE } from "src/flowsOverview/v2/folderModals/EditFolderModal";
import { useFolders } from "src/flowsOverview/v2/folderQueries";
import {
  FolderDropdownSelection,
  SelectFolderDropdown,
} from "src/flowsOverview/v2/SelectFolderDropdown";
import { TestRunConfigEditor } from "src/flowsOverview/v2/TestRunConfigEditor";
import { useCapabilities } from "src/hooks/useCapabilities";
import { nameValidations } from "src/layout/WorkspaceFormModal/utils";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { logger } from "src/utils/logger";
import {
  FLOW_SLUG_CONSTRAINTS,
  getStringValidations,
} from "src/utils/validations";

export type EditFlowModalOutputs = {
  name: string;
  wsId: string;
  slug: string;
  description?: string;
  orgId: string;
  folderSelection: FolderDropdownSelection;
  reviewConfig?: FlowReviewConfiguration;
  testRunConfig?: TestRunConfiguration;
};

export type EditFlowModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (outputs: EditFlowModalOutputs) => Promise<void>;
  onUnmount?: () => void;
  title: string;
  flow?: Flow;
  workspaceId: string;
  organizationId: string;
  initialFolderId?: string;
  mode: "add" | "edit";
};

export const EditFlowModal: React.FC<EditFlowModalProps> = ({
  open,
  onClose,
  onConfirm,
  onUnmount,
  title,
  flow,
  mode,
  workspaceId,
  organizationId,
  initialFolderId,
}) => {
  const [submitError, setSubmitError] = useState<boolean>(false);
  const folders = useFolders({ workspaceId });
  const { workspaceRole, flowFolders, flows } = useCapabilities();
  const canPickFolder = mode === "add" || flowFolders.canMoveFlows;
  const canPutFlowInFolder = (folderId: string) =>
    mode === "add" ? flows.canCreate(folderId) : flowFolders.canMoveFlows;
  const initialFolder =
    initialFolderId && canPutFlowInFolder(initialFolderId)
      ? initialFolderId
      : undefined;

  const {
    control,
    register,
    handleSubmit,
    formState,
    reset,
    setError,
    setValue,
    ...formMethods
  } = useForm<DecisionFlowInputs>({
    mode: "onChange",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    // Everytime the Modal is reopened with a new flow we need to reset the form to set the new default value
    if (open) {
      reset({
        name: flow?.name,
        description: flow?.meta?.description,
        slug: flow?.slug,
        selectFolder: {
          mode: "selectFolder",
          folderId: mode === "edit" ? flow?.flow_folder_id : initialFolder,
        },
        folderName: undefined,
        reviewConfig: flow?.review_configuration ?? {
          requires_review: false,
          requires_independent_review: false,
          default_reviewer_list: [],
          require_all_reviewers: false,
        },
        testRunConfig: flow?.test_run_configuration?.worker_count
          ? {
              worker_count: flow?.test_run_configuration.worker_count,
            }
          : undefined,
      });
    }
  }, [reset, flow, open, initialFolder, mode]);

  useEffect(() => onUnmount, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onModalClose = () => {
    setSubmitError(false);
    onClose();
  };

  const onModalConfirm = async (
    name: string,
    slug: string,
    description: string | undefined,
    folderSelection: FolderDropdownSelection,
    reviewConfig: FlowReviewConfiguration,
    testRunConfig: TestRunConfiguration,
  ) => {
    await onConfirm({
      name,
      wsId: workspaceId,
      slug,
      description,
      orgId: organizationId,
      folderSelection,
      reviewConfig,
      testRunConfig,
    });
    onModalClose();
  };

  const onSubmit = handleSubmit(async (data: DecisionFlowInputs) => {
    try {
      await onModalConfirm(
        data.name,
        data.slug,
        data.description,
        data.selectFolder,
        data.reviewConfig,
        data.testRunConfig,
      );
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.config?.url?.includes("/folders")) {
          setError(
            "selectFolder",
            { type: "notAvailable" },
            { shouldFocus: true },
          );
        } else if (e.config?.url?.includes("/flows")) {
          setError("slug", { type: "notAvailable" }, { shouldFocus: true });
        }
      } else {
        logger.error(e);
        setSubmitError(true);
      }
    }
  });

  return (
    <Modal open={open} size="sm" onClose={onModalClose}>
      <Modal.Header>{title}</Modal.Header>
      <form onSubmit={onSubmit}>
        <Modal.Content>
          <NameInput
            error={formState.errors.name}
            formProps={register("name", {
              required: true,
              validate: nameValidations,
              onChange: (e) => {
                if (mode === "add") {
                  setValue(
                    "slug",
                    slugify(e.target.value, { strict: true, lower: true }),
                    {
                      shouldValidate: true,
                    },
                  );
                }
              },
            })}
          />
          <SlugInput
            error={formState.errors.slug}
            formProps={register("slug", {
              required: mode === "add",
              disabled: mode === "edit",
              validate: getStringValidations(FLOW_SLUG_CONSTRAINTS),
            })}
          />
          {canPickFolder && (
            <>
              <div className="mt-3 mb-1">
                <p className="font-inter-semibold-13px text-gray-800">Folder</p>
              </div>
              <Controller
                control={control}
                name="selectFolder"
                render={({ field }) => (
                  <SelectFolderDropdown
                    folders={(folders.data?.folders || []).filter((folder) =>
                      canPutFlowInFolder(folder.id),
                    )}
                    selected={field.value || undefined}
                    onSelect={(value) => {
                      if (
                        value.mode === "selectFolder" &&
                        field.value?.mode === "selectFolder" &&
                        field.value?.folderId === value.folderId
                      ) {
                        field.onChange(null);
                      } else {
                        field.onChange(value);
                      }
                    }}
                  />
                )}
              />
              {formState.errors.selectFolder && (
                <ErrorHint>{FOLDER_NAME_NOT_AVAILABLE_MESSAGE}</ErrorHint>
              )}
            </>
          )}
          <div className="mt-3 mb-2 font-inter-semibold-13px text-gray-800">
            Description
          </div>
          <Textarea
            autoComplete="off"
            data-loc="add-description"
            placeholder="Enter description"
            {...register("description")}
          />
          <FormProvider
            {...formMethods}
            control={control}
            formState={formState}
            handleSubmit={handleSubmit}
            register={register}
            reset={reset}
            setError={setError}
            setValue={setValue}
          >
            {workspaceRole === WorkspaceSimpleRoleType.ADMIN && (
              <FlowReviewConfigEditor />
            )}
            {workspaceRole === WorkspaceSimpleRoleType.EDITOR &&
              flow?.review_configuration && (
                <FlowReviewConfigPreview
                  isEditingFlow={Boolean(flow)}
                  reviewConfig={flow.review_configuration}
                />
              )}
            {isFeatureFlagEnabled(FEATURE_FLAGS.testRunConfig) && (
              <div className="my-4">
                <TestRunConfigEditor />
              </div>
            )}
          </FormProvider>
          {submitError && (
            <ErrorHint>Error submitting the data - please try again</ErrorHint>
          )}
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="add-flow-save"
              isDisabled={!isEmpty(formState.errors)}
              htmlType="submit"
              isLoading={formState.isSubmitting}
              variant="primary"
            >
              {mode === "add" ? "Create" : "Save"}
            </Button>
          }
          secondaryButton="Cancel"
        />
      </form>
    </Modal>
  );
};
